import { useState } from "react";
import { useFetch } from "../hooks/useFetch";
import {
  getAllPaymentsByAdmin,
  getDailyReportByAdmin,
} from "../api/subscriptionsApi";
import "./css/Payments.css";
import AccessControl from "../components/AccessControl";

// ─── Deposit Status Badge helper ─────────────────────────────────────────────
function DepositBadge({ row }) {
  if (row.method !== "cash") return <span className="pay-muted">—</span>;
  if (row.bankVerified)
    return <span className="pay-badge pay-badge-verified">✅ Verified</span>;
  if (row.depositedToBank)
    return <span className="pay-badge pay-badge-deposited">🏦 Deposited</span>;
  return <span className="pay-badge pay-badge-not-deposited">⏳ Not Deposited</span>;
}

// ─── Daily Report Tab ─────────────────────────────────────────────────────────
function DailyReport() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);

  const { data, loading, error, refetch } = useFetch(
    () => getDailyReportByAdmin(date),
    [date],
  );

  const s = data?.summary;

  return (
    <div className="pay-daily-wrap">
      {/* Date picker */}
      <div className="pay-daily-bar">
        <label className="pay-daily-label">Report Date:</label>
        <input
          type="date"
          className="pay-daily-input"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
        />
        <button className="pay-daily-refresh" onClick={refetch}>↺ Refresh</button>
      </div>

      {loading ? (
        <div className="pay-skeleton">
          {[1, 2, 3].map((i) => <div key={i} className="pay-skeleton-row" />)}
        </div>
      ) : error ? (
        <p className="pay-error">{error}</p>
      ) : !s ? (
        <p className="pay-error">No data returned.</p>
      ) : (
        <>
          {/* Summary cards */}
          <div className="pay-daily-cards">
            <div className="pay-daily-card pay-card-online">
              <p className="pay-card-label">Online</p>
              <p className="pay-card-count">{s.onlineCount} payments</p>
              <p className="pay-card-amount">₹{s.onlineTotal?.toLocaleString()}</p>
            </div>
            <div className="pay-daily-card pay-card-cash">
              <p className="pay-card-label">Cash Collected</p>
              <p className="pay-card-count">{s.cashCount} payments</p>
              <p className="pay-card-amount">₹{s.cashTotal?.toLocaleString()}</p>
            </div>
            <div className="pay-daily-card pay-card-deposited">
              <p className="pay-card-label">Deposited to Bank</p>
              <p className="pay-card-count">{s.depositedCount} payments</p>
              <p className="pay-card-amount">₹{s.depositedTotal?.toLocaleString()}</p>
            </div>
            <div className="pay-daily-card pay-card-pending-dep">
              <p className="pay-card-label">Not Deposited</p>
              <p className="pay-card-count">{s.notDepositedCount} payments</p>
              <p className="pay-card-amount">₹{s.notDepositedTotal?.toLocaleString()}</p>
            </div>
            <div className="pay-daily-card pay-card-total">
              <p className="pay-card-label">Grand Total</p>
              <p className="pay-card-count">{s.onlineCount + s.cashCount} payments</p>
              <p className="pay-card-amount">₹{s.grandTotal?.toLocaleString()}</p>
            </div>
          </div>

          {/* Cash detail table */}
          {data.cash?.length > 0 && (
            <>
              <h3 className="pay-daily-sub">Cash Payments</h3>
              <div className="pay-table-card">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>Driver</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Collected By</th>
                      <th>Deposit Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cash.map((r) => (
                      <tr key={r._id}>
                        <td>
                          <p className="pay-driver-name">{r.driverId?.name || "—"}</p>
                          <p className="pay-driver-phone">{r.driverId?.phone || ""}</p>
                        </td>
                        <td><span className="pay-badge pay-badge-default">{r.planId?.name || "—"}</span></td>
                        <td><span className="pay-amount">₹{r.amount?.toLocaleString()}</span></td>
                        <td>{r.collectedBy?.name || <span className="pay-muted">—</span>}</td>
                        <td><DepositBadge row={r} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Payments Component ──────────────────────────────────────────────────
export default function Payments() {
  const [tab,     setTab]     = useState("all");   // 'all' | 'daily'
  const [filters, setFilters] = useState({ method: "", status: "" });
  const [page,    setPage]    = useState(1);

  const { data, loading, error } = useFetch(
    () => getAllPaymentsByAdmin({ ...filters, page, limit: 20 }),
    [filters, page],
  );

  const set = (key, val) => { setFilters((f) => ({ ...f, [key]: val })); setPage(1); };

  const rows       = data?.payments ?? data?.data ?? (Array.isArray(data) ? data : []);
  const total      = data?.total ?? data?.totalCount ?? rows.length;
  const totalPages = Math.ceil(total / 20);

  const getStatusBadge = (val) => {
    const map = {
      paid:     "pay-badge pay-badge-paid",
      pending:  "pay-badge pay-badge-pending",
      failed:   "pay-badge pay-badge-failed",
      rejected: "pay-badge pay-badge-failed",
    };
    return map[val] || "pay-badge pay-badge-default";
  };

  const getMethodBadge = (val) => {
    const map = {
      online: "pay-badge pay-badge-online",
      cash:   "pay-badge pay-badge-cash",
    };
    return map[val] || "pay-badge pay-badge-default";
  };

  return (
    <div className="pay-container">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "ACCOUNTS"]}
        redirectTo="/404"
      />

      {/* Header */}
      <div className="pay-header">
        <h1>Payments</h1>
        <p>All payment transactions</p>
      </div>

      {/* Tabs */}
      <div className="pay-tabs">
        <button
          className={`pay-tab ${tab === "all" ? "active" : ""}`}
          onClick={() => setTab("all")}
        >
          All Payments
        </button>
        <button
          className={`pay-tab ${tab === "daily" ? "active" : ""}`}
          onClick={() => setTab("daily")}
        >
          📅 Daily Report
        </button>
      </div>

      {/* ─── DAILY REPORT TAB ─── */}
      {tab === "daily" && <DailyReport />}

      {/* ─── ALL PAYMENTS TAB ─── */}
      {tab === "all" && (
        <>
          {/* Filters */}
          <div className="pay-filters">
            <div className="pay-filter-group">
              <span className="pay-filter-label">Method:</span>
              {["", "online", "cash"].map((m) => (
                <button
                  key={m}
                  onClick={() => set("method", m)}
                  className={`filter-pill ${filters.method === m ? "active" : ""}`}
                >
                  {m === "" ? "All" : m}
                </button>
              ))}
            </div>
            <div className="pay-filter-group">
              <span className="pay-filter-label">Status:</span>
              {["", "paid", "pending", "failed"].map((s) => (
                <button
                  key={s}
                  onClick={() => set("status", s)}
                  className={`filter-pill ${filters.status === s ? "active" : ""}`}
                >
                  {s === "" ? "All" : s}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="pay-skeleton">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="pay-skeleton-row" />
              ))}
            </div>
          ) : error ? (
            <p className="pay-error">{error}</p>
          ) : rows.length === 0 ? (
            <div className="pay-empty">
              <p className="pay-empty-icon">💳</p>
              <p className="pay-empty-title">No payments found</p>
              <p className="pay-empty-sub">Try changing the filters above</p>
            </div>
          ) : (
            <>
              <div className="pay-table-card">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>Driver</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Deposit Status</th>  {/* ✅ NEW COLUMN */}
                      <th>Collected By</th>
                      <th>Paid At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r._id}>
                        <td>
                          <p className="pay-driver-name">{r.driverId?.name || "—"}</p>
                          <p className="pay-driver-phone">{r.driverId?.phone || ""}</p>
                        </td>
                        <td>
                          <span className="pay-badge pay-badge-default">
                            {r.planId?.name || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="pay-amount">₹{r.amount?.toLocaleString()}</span>
                        </td>
                        <td>
                          <span className={getMethodBadge(r.method)}>{r.method}</span>
                        </td>
                        <td>
                          <span className={getStatusBadge(r.status)}>{r.status}</span>
                        </td>
                        {/* ✅ NEW: Deposit status column */}
                        <td>
                          <DepositBadge row={r} />
                        </td>
                        <td>
                          {r.collectedBy?.name || <span className="pay-muted">—</span>}
                        </td>
                        <td>
                          {r.paidAt
                            ? new Date(r.paidAt).toLocaleString("en-IN")
                            : <span className="pay-muted">Pending</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pay-pagination">
                  <button
                    className="page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ← Prev
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "..." ? (
                        <span key={`dots-${idx}`} className="page-dots">…</span>
                      ) : (
                        <button
                          key={item}
                          className={`page-btn ${page === item ? "active" : ""}`}
                          onClick={() => setPage(item)}
                        >
                          {item}
                        </button>
                      ),
                    )}

                  <button
                    className="page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}